import { Slot } from '@radix-ui/react-slot';
import { type VariantProps, cva } from 'class-variance-authority';
import { forwardRef, type ButtonHTMLAttributes } from 'react';

import { cn } from '../../lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        primary:
          'bg-accent-primary text-text-inverse hover:bg-accent-primary-hover active:bg-accent-primary-active',
        secondary:
          'bg-bg-secondary text-text-primary hover:bg-bg-tertiary border border-silver-primary',
        ghost: 'bg-transparent hover:bg-bg-tertiary text-text-secondary hover:text-text-primary',
        danger: 'bg-status-error text-text-inverse hover:opacity-90',
      },
      size: {
        md: 'h-10 px-5 py-2.5',
        sm: 'h-9 px-4 py-2',
        icon: 'h-9 w-9 p-0',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  readonly asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant, size, asChild = false, ...props },
  ref,
) {
  const Comp = asChild ? Slot : 'button';
  return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
});
